// CustomerNoteBox — Phase 6d. Per-customer scratchpad in the sidebar.
//
// Renders a compact labelled textarea keyed on the conversation's customer
// email. Loads the note on mount (via RLS-backed anon client), renders
// "saved" feedback on successful upsert, and debounces writes so Kimi can
// type continuously without hammering Postgres.
//
// No note content propagates across sessions via local state — we always
// trust the query cache.

import { useEffect, useRef, useState } from 'react';
import {
  useCustomerNote,
  useSaveCustomerNote,
} from '@/lib/hooks/useCustomerNote';

type CustomerNoteBoxProps = {
  customerEmail: string | null | undefined;
};

// How long after Kimi stops typing before we save. Short enough that a
// tab-away saves, long enough that a single character press doesn't
// trigger a network round-trip.
const DEBOUNCE_MS = 800;

export function CustomerNoteBox({ customerEmail }: CustomerNoteBoxProps) {
  const { data } = useCustomerNote(customerEmail);
  const saveMutation = useSaveCustomerNote(customerEmail);

  const [value, setValue] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const lastSavedRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  // Reset local state whenever the customer email changes (new thread),
  // and seed from the query's latest data as soon as it arrives.
  useEffect(() => {
    initializedRef.current = false;
    setValue('');
    setSavedAt(null);
    lastSavedRef.current = '';
  }, [customerEmail]);

  useEffect(() => {
    if (initializedRef.current) return;
    const incoming = data?.note ?? '';
    setValue(incoming);
    lastSavedRef.current = incoming;
    initializedRef.current = true;
  }, [data]);

  // Debounced save. Fires DEBOUNCE_MS after the last keystroke if the
  // current value differs from what we last saved. Skips if there's no
  // customer email to key on.
  useEffect(() => {
    if (!customerEmail) return;
    if (!initializedRef.current) return;
    if (value === lastSavedRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveMutation.mutate(value, {
        onSuccess: () => {
          lastSavedRef.current = value;
          setSavedAt(Date.now());
        },
      });
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, customerEmail]);

  if (!customerEmail) return null;

  const showSaved =
    savedAt !== null && Date.now() - savedAt < 3000 && value === lastSavedRef.current;

  return (
    <section
      className="sb-card"
      aria-label="Customer note"
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div className="sb-eyebrow" style={{ margin: 0 }}>
          Customer note
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'hsl(var(--ink) / 0.45)',
            minHeight: 14,
          }}
        >
          {saveMutation.isPending
            ? 'Saving…'
            : showSaved
              ? 'Saved'
              : saveMutation.isError
                ? 'Save failed'
                : ''}
        </div>
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`Private notes about ${customerEmail}…`}
        rows={3}
        style={{
          width: '100%',
          minHeight: 64,
          resize: 'vertical',
          padding: '8px 10px',
          border: '1px solid hsl(var(--ink) / 0.12)',
          borderRadius: 6,
          background: 'hsl(var(--eggshell) / 0.5)',
          fontSize: 13,
          lineHeight: 1.45,
          color: 'hsl(var(--ink))',
          fontFamily: 'inherit',
        }}
      />
      <div
        style={{
          fontSize: 11,
          color: 'hsl(var(--ink) / 0.45)',
          lineHeight: 1.3,
        }}
      >
        Only you see these notes. They follow this email across every thread.
      </div>
    </section>
  );
}
